// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { api } from '../../api/client';
import type { InstanceInfo, Room, SolidSlab, SolidSpace, Storey } from '../../api/types';
import { isTerminal } from '../../api/types';
import { useJobs } from '../../app/JobsContext';
import { useAsync } from '../../app/useAsync';

export interface ServerSpatial {
  /** `GET /instances/{cloud}` rows (with `centroid`/`bbox` on ruxd-6tz); null until read, or when it failed. */
  instances: InstanceInfo[] | null;
  /** `GET /rooms`; null until read, without a rooms cloud, or on a server without the route. */
  rooms: Room[] | null;
  /** `GET /storeys`; null until read, or on a server without the route. */
  storeys: Storey[] | null;
  /** `GET /spaces` (the building model's rooms, ruxd#11); null until read, or on a server without the route. */
  spaces: SolidSpace[] | null;
  /** `GET /slabs` (the building model's floor slabs and roof, ruxd-jmt); null until read, or on a server without the route. */
  slabs: SolidSlab[] | null;
}

/** A read whose failure (an older server's 404 included) is simply "not known". */
function orNull<T>(run: (signal: AbortSignal) => Promise<T>): (signal: AbortSignal) => Promise<T | null> {
  return async (signal) => {
    try {
      return await run(signal);
    } catch (cause) {
      if (signal.aborted) throw cause;
      return null;
    }
  };
}

/**
 * The spatial structure the server computes over the whole cloud
 * (CONTRACT-GAPS gaps 6, 7, 8), read independently of the stream so the
 * Ground has rooms, storeys and click targets before the cloud arrives.
 * Re-read when the clouds behind it are rewritten (`clouds.changed`) or a
 * job finishes (a new Rum / Etager / Instanser run), and the storeys and
 * spaces on `model.changed` (an edit). Everything resolves
 * null on a server without the routes; the Ground then keeps its streamed
 * heuristics (`bygning/spatial.ts`).
 */
export function useServerSpatial(instCloud: string | null, roomCloud: string | null): ServerSpatial {
  const { cloudRevisions, jobs, modelChange } = useJobs();
  const finished = jobs.filter((j) => isTerminal(j.status)).length;
  // A model edit (Rediger › Geometri, ruxd-5uv) rewrites storeys and spaces without a job.
  const edited = modelChange?.revision ?? null;
  const instRev = instCloud ? (cloudRevisions[instCloud] ?? 0) : 0;
  const roomRev = roomCloud ? (cloudRevisions[roomCloud] ?? 0) : 0;

  const instancesRead = useAsync(
    orNull((s) => (instCloud ? api.instances(instCloud, s) : Promise.resolve(null))),
    [instCloud, instRev, finished],
  );
  const roomsRead = useAsync(
    orNull((s) => (roomCloud ? api.rooms(s) : Promise.resolve(null))),
    [roomCloud, roomRev, finished],
  );
  const storeysRead = useAsync(
    orNull((s) => api.storeys(s)),
    [roomRev, finished, edited],
  );

  const spacesRead = useAsync(
    orNull((s) => api.spaces(s)),
    [finished, edited],
  );
  const slabsRead = useAsync(
    orNull((s) => api.slabs(s)),
    [finished],
  );

  return {
    spaces: spacesRead.data ?? null,
    slabs: slabsRead.data ?? null,
    instances: instancesRead.data ?? null,
    rooms: roomsRead.data ?? null,
    storeys: storeysRead.data ?? null,
  };
}
